import { BottomNavBar } from "@phaser/ui/navs/BottomNavBar";
import { CodexScrollView } from "@phaser/ui/codex/CodexScrollView";
import { getAllMonsters } from "@/phaser/core/monsters/monsterCatalog";
import { ModalType, ModalManager } from "@phaser/ui/modals/ModalManager";
import { monsterCountManager } from "@/phaser/core/monsters/MonsterCountManager";
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";
import { App } from "@capacitor/app";
import { AudioManager } from "../core/audio/AudioManager";
import { t } from "@/i18n/i18n";

export class CodexScene extends Phaser.Scene {
  private modalManager!: ModalManager;
  private backSub?: PluginListenerHandle;
  private isMonsterDetailOpen = false;

  constructor() {
    super("CodexScene");
  }

  // preload() {
  // }

  async create() {
    const { width, height } = this.scale;

    this.modalManager = new ModalManager(this);

    const monsters = getAllMonsters();

    // 발견한 몬스터 ID 목록 (분류 횟수 > 0)
    const discoveredMonsterIdSet = new Set(
      monsters
        .filter((monster) => monsterCountManager.getCount(monster.id) > 0)
        .map((monster) => monster.id),
    );

    const discoveredCount = discoveredMonsterIdSet.size;

    // 배경
    const bg = this.add.image(width / 2, height / 2, "codex-bg");
    bg.setDisplaySize(width, height);

    this.add
      .text(width / 2, height / 2 - 550, "codex", {
        fontFamily: "Luckiest Guy",
        fontSize: "120px",
        fontStyle: "normal",
        color: "#272829",
        stroke: "#D8D9DA",
        strokeThickness: 20,
      })
      .setOrigin(0.5);

    this.add
      .text(
        width - 14,
        height / 2 - 400,
        t("codex.discovered_monsters", {
          discoveredCount,
          totalCount: monsters.length,
        }),
        {
          fontFamily: "Black Han Sans",
          fontSize: "50px",
          color: "#FFFFFF",
          align: "right",
        },
      )
      .setOrigin(1, 0.5);

    // 몬스터 리스트 스크롤 뷰
    new CodexScrollView(this, {
      x: 0,
      y: height / 2 - 330,
      width,
      height: height - 538,
      discoveredMonsterIds: discoveredMonsterIdSet,
    });

    // 하단 네비게이션
    new BottomNavBar(this, width / 2, height, "CODEX");

    // 몬스터 카드 열기 이벤트 리스너
    this.events.on("open-monster-detail", this.handleOpenMonsterDetail, this);
    // 몬스터 카드 닫기 이벤트 리스너
    this.events.on(
      "monster-detail-closed",
      this.handleMonsterDetailClosed,
      this,
    );

    // 네이티브 앱에서 뒤로가기 버튼 처리 (몬스터 카드 모달이 열려있을 때 닫기)
    if (Capacitor.isNativePlatform()) {
      this.registerBackButton();

      this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        this.backSub?.remove();
        this.backSub = undefined;
      });
    }

    // 씬 종료 시 이벤트 정리
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(
        "open-monster-detail",
        this.handleOpenMonsterDetail,
        this,
      );
      this.events.off(
        "monster-detail-closed",
        this.handleMonsterDetailClosed,
        this,
      );
      this.modalManager.close();
      this.isMonsterDetailOpen = false;
    });
  }

  // 몬스터 카드 상세보기 이벤트 핸들러
  private handleOpenMonsterDetail(data: { monsterId: string }) {
    AudioManager.playSfx("sfx-slide");

    this.isMonsterDetailOpen = true;

    this.modalManager.open(ModalType.MONSTER_CARD, {
      monsterId: data.monsterId,
    });
  }

  // 몬스터 카드 모달이 닫힐 때 상태 업데이트
  private handleMonsterDetailClosed() {
    this.isMonsterDetailOpen = false;
  }

  // 네이티브 앱 뒤로가기 버튼 등록
  private async registerBackButton() {
    this.backSub = await App.addListener("backButton", () => {
      if (this.isMonsterDetailOpen) {
        this.modalManager.close();
        this.isMonsterDetailOpen = false;
      }
    });
  }
}
